import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { auditApi } from '../services/api';
import { formatTimestamp } from '../utils/format';
import RawJsonModal from '../components/RawJsonModal';

function TransactionDetail() {
    const { transactionId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedChanges, setExpandedChanges] = useState(new Set());

    useEffect(() => {
        loadDetail();
    }, [transactionId]);

    const loadDetail = async () => {
        try {
            setLoading(true);
            const res = await auditApi.getTransactionDetail(transactionId);
            setData(res.data);
            setError(null);
            // Auto-expand all changes
            if (res.data?.changes) {
                setExpandedChanges(new Set(res.data.changes.map((_, i) => i)));
            }
        } catch (err) {
            setError('Failed to load transaction detail.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleChange = (index) => {
        setExpandedChanges(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    if (loading) {
        return <div className="text-center py-12 text-gray-500">Loading transaction detail...</div>;
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <div className="text-red-600">{error}</div>
                <Link to="/transactions" className="mt-4 inline-block text-blue-600 hover:underline">
                    ← Back to transactions
                </Link>
            </div>
        );
    }

    if (!data) return null;

    const { transaction, changes, changesByTable } = data;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/transactions" className="text-blue-600 hover:underline text-sm">
                        ← Back to transactions
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900 mt-2">Transaction Detail</h1>
                </div>
            </div>

            {/* Transaction Info Card */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Transaction ID</h3>
                        <p className="text-sm font-mono text-gray-900 break-all">{transaction.transactionId}</p>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Source</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            transaction.source === 'GUI'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}>
                            {transaction.source || 'UNKNOWN'}
                        </span>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">GUI Function</h3>
                        <p className="text-sm text-gray-900">{transaction.guiFunctionName || 'N/A'}</p>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Commit Time</h3>
                        <p className="text-sm text-gray-900">
                            {formatTimestamp(transaction.commitTimestamp)}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Tables Affected</h3>
                        <div className="flex flex-wrap gap-1">
                            {transaction.tablesAffected?.map((t, i) => (
                                <span key={i} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                    {t.table} ({t.operation} ×{t.rowCount})
                                </span>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Total Changes</h3>
                        <p className="text-sm text-gray-900">
                            {transaction.totalTableChanges} tables, {transaction.totalFieldChanges} field changes
                        </p>
                    </div>
                </div>

                {/* Metadata */}
                {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-medium text-gray-500 mb-3">Metadata</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.entries(transaction.metadata).map(([key, value]) => (
                                <div key={key}>
                                    <span className="text-xs text-gray-500">{key}</span>
                                    <p className="text-sm text-gray-900">{value || '-'}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Changes grouped by table */}
            {changesByTable && Object.entries(changesByTable).map(([tableKey, tableChanges]) => (
                <div key={tableKey} className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900">
                            📋 {tableKey}
                            <span className="ml-2 text-sm text-gray-500">
                                ({tableChanges.length} change{tableChanges.length > 1 ? 's' : ''})
                            </span>
                        </h2>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {tableChanges.map((change, idx) => (
                            <ChangeRecord key={idx} change={change} />
                        ))}
                    </div>
                </div>
            ))}

            {/* Flat change list (fallback) */}
            {(!changesByTable || Object.keys(changesByTable).length === 0) && changes && (
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900">
                            📋 All Changes ({changes.length})
                        </h2>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {changes.map((change, idx) => (
                            <ChangeRecord key={idx} change={change} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ChangeRecord({ change }) {
    const [expanded, setExpanded] = useState(true);
    const [showRaw, setShowRaw] = useState(false);
    const changedFields = change.fieldChanges?.filter(f => f.changed) || [];
    const unchangedFields = change.unchangedFields || [];

    return (
        <div className="p-6">
            {/* Change Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                    <OperationBadge operation={change.operation} />
                    <span className="text-sm font-mono text-gray-600">
                        {change.recordIdentifier || `${change.schema}.${change.table}`}
                    </span>
                    <span className="text-xs text-gray-400">
                        #{change.sequenceNo}
                    </span>
                </div>
                <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                        {changedFields.length} field{changedFields.length > 1 ? 's' : ''} changed
                    </span>
                    {change.rawCdcEvent && (
                        <button
                            onClick={() => setShowRaw(true)}
                            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                            title="View raw CDC event data"
                        >
                            📄 Raw
                        </button>
                    )}
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        {expanded ? '▼' : '▶'}
                    </button>
                </div>
            </div>

            {showRaw && <RawJsonModal rawJson={change.rawCdcEvent} onClose={() => setShowRaw(false)} />}

            {/* Primary Keys */}
            {change.primaryKeys && Object.keys(change.primaryKeys).length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                    {Object.entries(change.primaryKeys).map(([key, value]) => (
                        <span key={key} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded font-mono">
                            {key}: {String(value)}
                        </span>
                    ))}
                </div>
            )}

            {/* Field Changes Table */}
            {expanded && changedFields.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-1/5">Field</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-2/5">
                                    Old Value
                                </th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-12">→</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-2/5">
                                    New Value
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {changedFields.map((field, i) => (
                                <tr key={i} className="diff-changed hover:bg-gray-50">
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900 font-mono">
                                        {field.field}
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        <span className={`inline-block px-2 py-1 rounded text-sm font-mono diff-old ${
                                            change.operation === 'INSERT' ? 'opacity-30' : ''
                                        }`}>
                                            {field.oldValue === null || field.oldValue === undefined
                                                ? <span className="italic text-gray-400">NULL</span>
                                                : String(field.oldValue)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-center text-gray-400">→</td>
                                    <td className="px-4 py-2 text-sm">
                                        <span className={`inline-block px-2 py-1 rounded text-sm font-mono diff-new ${
                                            change.operation === 'DELETE' ? 'opacity-30' : ''
                                        }`}>
                                            {field.newValue === null || field.newValue === undefined
                                                ? <span className="italic text-gray-400">NULL</span>
                                                : String(field.newValue)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Unchanged fields (collapsible) */}
            {expanded && unchangedFields.length > 0 && (
                <details className="mt-3">
                    <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                        {unchangedFields.length} unchanged field{unchangedFields.length > 1 ? 's' : ''}
                    </summary>
                    <div className="mt-2 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100 border border-gray-100 rounded text-xs">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-1 text-left text-gray-500">Field</th>
                                    <th className="px-3 py-1 text-left text-gray-500">Value</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {unchangedFields.map((field, i) => (
                                    <tr key={i}>
                                        <td className="px-3 py-1 font-mono text-gray-600">{field.field}</td>
                                        <td className="px-3 py-1 text-gray-500">{String(field.value ?? 'NULL')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}

            {/* No changes (rare) */}
            {expanded && changedFields.length === 0 && (
                <div className="text-sm text-gray-500 italic">No field changes detected</div>
            )}
        </div>
    );
}

function OperationBadge({ operation }) {
    const styles = {
        INSERT: 'bg-green-100 text-green-800 border-green-300',
        UPDATE: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        DELETE: 'bg-red-100 text-red-800 border-red-300',
        READ: 'bg-blue-100 text-blue-800 border-blue-300',
    };

    const icons = {
        INSERT: '➕',
        UPDATE: '✏️',
        DELETE: '🗑️',
        READ: '👁️',
    };

    return (
        <span className={`px-2 py-1 text-xs font-medium rounded border ${styles[operation] || 'bg-gray-100 text-gray-800'}`}>
            {icons[operation] || ''} {operation}
        </span>
    );
}

export default TransactionDetail;
